import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  CheckCircle,
  Calendar,
  MapPin,
  Hotel,
  ArrowRight,
  Printer,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

export default function BookingConfirmationPage() {
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('bookingConfirmation');
    if (!stored) {
      navigate('/portal/dashboard');
      return;
    }
    setConfirmation(JSON.parse(stored));
  }, [navigate]);

  if (!confirmation) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30" data-testid="booking-confirmation-page">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2">
            <Hotel className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold">HMS Hotels</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-4">
              <CheckCircle className="h-10 w-10 text-success" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Booking Confirmed!</h1>
            <p className="text-muted-foreground">
              Your reservation has been successfully created
            </p>
          </div>

          {/* Confirmation Card */}
          <Card className="text-left mb-8">
            <CardHeader className="text-center border-b">
              <CardTitle className="text-lg">Confirmation Number</CardTitle>
              <p className="text-3xl font-mono font-bold text-primary" data-testid="confirmation-number">
                {confirmation.confirmation_number}
              </p>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Property */}
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-muted shrink-0">
                  <Hotel className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold">{confirmation.property?.name}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {confirmation.property?.address_city}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Check-in</p>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {format(new Date(confirmation.arrival_date), 'EEE, MMM d, yyyy')}
                  </p>
                  <p className="text-sm text-muted-foreground">From 2:00 PM</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Check-out</p>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {format(new Date(confirmation.departure_date), 'EEE, MMM d, yyyy')}
                  </p>
                  <p className="text-sm text-muted-foreground">Until 11:00 AM</p>
                </div>
              </div>

              <Separator />

              {/* Room */}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Room Type</p>
                <p className="font-medium">{confirmation.room_type?.name}</p>
              </div>

              <Separator />

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Amount</span>
                <span className="text-2xl font-bold">
                  ${confirmation.grand_total?.toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              Print Confirmation
            </Button>
            <Button onClick={() => navigate('/portal/dashboard')} data-testid="go-to-dashboard-btn">
              Go to My Reservations
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Info Box */}
          <Card className="mt-8 bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3 text-left">
                <Mail className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Confirmation email sent</p>
                  <p className="text-sm text-muted-foreground">
                    A confirmation email has been sent to your registered email address with all the booking details.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
